import {Button, Chip} from "@mui/material";
import {
	DataTable,
	DataTableCellProps,
	useDataTableStorage,
	useSpringPage,
	useSpringPageable,
	useStorage,
} from "@variocube/app-ui";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import React, {useMemo} from "react";
import {Link as RouterLink} from "react-router-dom";
import {Subscription} from "../../api/client";
import {useSubscriptionApi, SubscriptionQuery} from "../../api/subscription";
import {EmptyList} from "../../controls";
import {TFunc, useLocalization} from "@smalox/shared";
import {queryKeys} from "../../utils/queryKeys";
import {View, ViewHeader} from "../View";
import {SubscriptionFilter} from "./SubscriptionFilter";
import {SubscriptionCancel} from "./SubscriptionCancel";
import {PlanLinkChip} from "../plan/PlanChip";

type SubscriptionFilterState = Omit<SubscriptionQuery, "page" | "size" | "sort">;

const defaultFilter: SubscriptionFilterState = {active: true};

export function SubscriptionList() {
	const {t} = useLocalization();
	const queryClient = useQueryClient();
	const {onPageChange, onSort, ...storage} = useDataTableStorage("SubscriptionList.paging");
	const [filter, setFilter] = useStorage<SubscriptionFilterState>("SubscriptionList.filter", defaultFilter);
	const pageable = useSpringPageable(storage);

	const query = useMemo(() => ({
		...filter,
		...pageable,
	}), [filter, pageable]);

	const {querySubscriptions} = useSubscriptionApi();
	const {data, isLoading, error} = useQuery(
		queryKeys.subscriptions.list(query),
		() => querySubscriptions(query),
	);

	const {rows, page} = useSpringPage(data);

	const onRefresh = () => queryClient.invalidateQueries(queryKeys.subscriptions.all());
	const columns = useMemo(() => createColumns(t, onRefresh), [t, query]);

	return (
		<View>
			<ViewHeader
				title={t("subscriptions.title")}
				actions={
					<Button variant="contained" color="primary" component={RouterLink} to="new">
						{t("subscriptions.create.title")}
					</Button>
				}
			/>
			<SubscriptionFilter
				value={filter}
				onChange={setFilter}
			/>
			<DataTable
				columns={columns}
				rows={rows}
				page={page}
				loading={isLoading}
				error={error}
				onSort={onSort}
				onPageChange={onPageChange}
				empty={<EmptyList />}
				{...storage}
			/>
		</View>
	);
}

function createColumns(t: TFunc, onRefresh: () => void) {
	return [
		{
			label: t("subscriptions.tenant"),
			field: "tenantName" as const,
			sortable: true,
			component: ({row}: DataTableCellProps<Subscription>) => <>{row.tenantName}</>,
		},
		{
			label: t("subscriptions.plan"),
			field: "planName" as const,
			sortable: true,
			component: ({row}: DataTableCellProps<Subscription>) => (
				<PlanLinkChip plan={{id: row.planId, name: row.planName, features: row.planFeatures ?? []}} />
			),
		},
		{
			label: t("subscriptions.startDate"),
			field: "startDate" as const,
			sortable: true,
		},
		{
			label: t("subscriptions.endDate"),
			field: "endDate" as const,
			sortable: true,
			component: ({row}: DataTableCellProps<Subscription>) => (
				row.endDate
					? <>{row.endDate}</>
					: <Chip label={t("subscriptions.status.active")} color="success" size="small" />
			),
		},
		{
			label: "",
			field: "id" as const,
			component: ({row}: DataTableCellProps<Subscription>) => (
				!row.endDate && row.id
					? <SubscriptionCancel subscriptionId={row.id} onCancelled={onRefresh} />
					: null
			),
		},
	];
}
